import { Card, Form, Button, Switch, InputNumber, message, Input } from "antd"
import * as API from "common/api"
import * as COMMONS from "common/common"
import "moment/locale/ja"
import React, { useEffect } from "react"
import { useMutation, useQueryClient } from "react-query"
import { Helmet } from "react-helmet-async"
import { useNavigate } from "react-router-dom"

const Shipment = (props) => {
  const { systemSettings } = props

  const navigate = useNavigate()

  const isMountedRef = COMMONS.USE_IS_MOUNTED_REF()
  const queryClient = useQueryClient()

  const [freeShipmentSettingsForm] = Form.useForm()
  const [shipmentTrackingUrlSettingsForm] = Form.useForm()

  const freeShipmentSettingsMutation = useMutation(API.ADMIN_UPDATE_SETTINGS, {
    onSuccess: () => {
      if (isMountedRef.current) {
        message.success(COMMONS.SUCCESS_SAVE_MSG)
        queryClient.invalidateQueries(API.QUERY_KEY_ADMIN_SETTINGS)
      }
    },
    onError: (error) => {
      if (error?.response?.status === COMMONS.RESPONSE_PERMISSION_ERROR) {
        navigate(COMMONS.PERMISSION_ERROR_ROUTE)
      } else if (error?.response?.status === COMMONS.RESPONSE_SESSION_ERROR) {
        message.warning(COMMONS.ERROR_SESSION_MSG)
        navigate(COMMONS.ADMIN_LOGIN_ROUTE)
      } else if (error?.response?.status === COMMONS.RESPONSE_SYSTEM_ERROR) {
        message.error(COMMONS.ERROR_SYSTEM_MSG)
      } else {
        message.error(COMMONS.ERROR_SYSTEM_MSG)
      }
    },
  })

  const shipmentTrackingUrlSettingsMutation = useMutation(
    API.ADMIN_UPDATE_SETTINGS,
    {
      onSuccess: () => {
        if (isMountedRef.current) {
          message.success(COMMONS.SUCCESS_SAVE_MSG)
          queryClient.invalidateQueries(API.QUERY_KEY_ADMIN_SETTINGS)
        }
      },
      onError: (error) => {
        if (error?.response?.status === COMMONS.RESPONSE_PERMISSION_ERROR) {
          navigate(COMMONS.PERMISSION_ERROR_ROUTE)
        } else if (error?.response?.status === COMMONS.RESPONSE_SESSION_ERROR) {
          message.warning(COMMONS.ERROR_SESSION_MSG)
          navigate(COMMONS.ADMIN_LOGIN_ROUTE)
        } else if (error?.response?.status === COMMONS.RESPONSE_SYSTEM_ERROR) {
          message.error(COMMONS.ERROR_SYSTEM_MSG)
        } else {
          message.error(COMMONS.ERROR_SYSTEM_MSG)
        }
      },
    }
  )

  useEffect(() => {
    freeShipmentSettingsForm.setFieldsValue({
      freeShipmentEnabled:
        systemSettings?.freeShipmentEnabled?.valueFlag || false,
      freeShipmentFrom: systemSettings?.freeShipmentFrom?.valueNumber || 0,
    })

    shipmentTrackingUrlSettingsForm.setFieldsValue({
      shipmentTrackingUrl:
        systemSettings?.shipmentTrackingUrl?.valueString || undefined,
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [systemSettings])

  const handleFreeShipmentSettings = (data) => {
    const paramData = [
      {
        label: API.SETTINGS_LABEL_FREE_SHIPMENT_ENABLED,
        settingName: API.SETTINGS_KEY_FREE_SHIPMENT_ENABLED,
        value: data.freeShipmentEnabled,
        isPublic: true,
      },
      ...(data.freeShipmentEnabled
        ? [
            {
              label: API.SETTINGS_LABEL_FREE_SHIPMENT_FROM,
              settingName: API.SETTINGS_KEY_FREE_SHIPMENT_FROM,
              value: data.freeShipmentFrom,
              isPublic: true,
            },
          ]
        : []),
    ]

    freeShipmentSettingsMutation.mutate(paramData)
  }

  const handleShipmentTrackingUrlSettings = (data) => {
    const paramData = [
      {
        label: API.SETTINGS_LABEL_SHIPMENT_TRACKING_URL,
        settingName: API.SETTINGS_KEY_SHIPMENT_TRACKING_URL,
        value: data.shipmentTrackingUrl,
        isPublic: true,
      },
    ]

    shipmentTrackingUrlSettingsMutation.mutate(paramData)
  }

  return (
    <>
      <Helmet>
        <title>配達設定 | {process.env.REACT_APP_SYSTEM_NAME}</title>
      </Helmet>
      <Card
        title="配達設定"
        className="animate__animated animate__fadeIn"
        bordered={false}
      >
        <Card
          type="inner"
          title="送料無料設定"
          extra={
            <Button
              type="primary"
              size="large"
              className="w-32 m-1"
              onClick={() => {
                freeShipmentSettingsForm.submit()
              }}
            >
              保存
            </Button>
          }
        >
          <Form
            form={freeShipmentSettingsForm}
            layout="horizontal"
            labelCol={{ span: 8 }}
            wrapperCol={{ span: 16 }}
            labelAlign="left"
            initialValues={{
              freeShipmentEnabled: false,
              freeShipmentFrom: 0,
            }}
            onFinish={handleFreeShipmentSettings}
            requiredMark={false}
            colon={false}
            size="large"
            scrollToFirstError
          >
            <Form.Item
              name="freeShipmentEnabled"
              label="送料無料"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => {
                return (
                  prevValues.freeShipmentEnabled !==
                  currentValues.freeShipmentEnabled
                )
              }}
            >
              {({ getFieldValue }) =>
                getFieldValue("freeShipmentEnabled") ? (
                  <>
                    <Form.Item label="送料無料条件">
                      <Form.Item
                        name="freeShipmentFrom"
                        rules={[
                          {
                            required: true,
                            message: "必須です",
                          },
                        ]}
                      >
                        <InputNumber
                          placeholder="例：2000"
                          addonBefore="￥"
                          addonAfter="から無料"
                          min={0}
                          type="number"
                          onPressEnter={(e) => {
                            e.preventDefault()
                          }}
                        />
                      </Form.Item>
                    </Form.Item>
                  </>
                ) : (
                  ""
                )
              }
            </Form.Item>
          </Form>
        </Card>
        <Card
          type="inner"
          title="荷物問い合わせ先URL設定"
          className="mt-8"
          extra={
            <Button
              type="primary"
              size="large"
              className="w-32 m-1"
              onClick={() => {
                shipmentTrackingUrlSettingsForm.submit()
              }}
            >
              保存
            </Button>
          }
        >
          <Form
            form={shipmentTrackingUrlSettingsForm}
            layout="horizontal"
            labelCol={{ span: 8 }}
            wrapperCol={{ span: 16 }}
            labelAlign="left"
            initialValues={{
              shipmentTrackingUrl: undefined,
            }}
            onFinish={handleShipmentTrackingUrlSettings}
            requiredMark={false}
            colon={false}
            size="large"
            scrollToFirstError
          >
            <Form.Item
              name="shipmentTrackingUrl"
              label="荷物問い合わせ先URL"
              rules={[
                {
                  whitespace: true,
                  message: "必須です",
                },
                {
                  type: "url",
                  message: "URLが有効なURLではありません",
                },
              ]}
            >
              <Input
                placeholder="例：http://k2k.sagawa-exp.co.jp/p/web/okurijosearch.do?okurijoNo=送り状番号"
                onPressEnter={(e) => e.preventDefault()}
                className="w-full"
                allowClear
              />
            </Form.Item>
            <Form.Item label="内容" className="my-4">
              <div className="p-4 bg-yellow-200 text-sm rounded break-all">
                <p className="whitespace-pre-wrap">{`荷物問い合わせシステム確認URLの送り状番号が入る場所に「送り状番号」と入れてたら送り状番号に切り替えることができます。`}</p>
                <div className="mt-4">
                  <p className="font-bold">
                    例：クロネコヤマトの荷物お問い合わせシステム
                  </p>
                  <p>
                    http://jizen.kuronekoyamato.co.jp/jizen/servlet/crjz.b.NQ0010?id=送り状番号
                  </p>
                </div>
                <div className="mt-4">
                  <p className="font-bold">例：個別番号検索 – 日本郵便</p>
                  <p>
                    https://trackings.post.japanpost.jp/services/srv/search/?requestNo1=送り状番号&search=追跡スタート
                  </p>
                </div>
                <div className="mt-4">
                  <p className="font-bold">
                    例：佐川急便 – 【お荷物問い合わせサービス】
                  </p>
                  <p>
                    http://k2k.sagawa-exp.co.jp/p/web/okurijosearch.do?okurijoNo=送り状番号
                  </p>
                </div>
                <div className="mt-4">
                  <p className="font-bold">
                    例：お荷物配達状況照会・お問い合わせ番号から照会｜福山通運
                  </p>
                  <p>
                    https://corp.fukutsu.co.jp/situation/tracking_no_hunt/送り状番号
                  </p>
                </div>
              </div>
            </Form.Item>
          </Form>
        </Card>
      </Card>
    </>
  )
}

export default Shipment
